/**
 * شمارشِ دسته‌های صفحه‌ی کشف، و تصمیمِ «کدوم‌ها روی صفحه‌ی اول بیان».
 *
 * دو تا عددِ کاملاً متفاوت این‌جا حساب می‌شن و قاطی کردنشون بدترین کاریه که
 * می‌شه کرد:
 *
 *  - `count` — عددی که کنارِ آیکون **دیده می‌شه**: چند آیتمِ همین فهرست این
 *    تگ رو دارن. یه رخدادِ «موسیقی + راک» توی هر دو شمرده می‌شه، چون کاربر با
 *    زدنِ هر کدوم همون رخداد رو می‌بینه؛ پس مجموعِ عددها می‌تونه از تعدادِ
 *    آیتم‌ها بیشتر باشه. اشکالی نداره — عدد قرار نیست جمع بشه.
 *
 *    «همین فهرست» یعنی هرچی روی صفحه‌ست، از جمله بعدِ انتخابِ یه دسته: با
 *    زدنِ «موسیقی»، عددها می‌شن «چند تا از رخدادهای موسیقی این تگ رو هم
 *    دارن» و ردیف تبدیل می‌شه به یه فهرستِ زیرشاخه‌ها.
 *
 *  - `mergeCount` — عددی که **هیچ‌وقت نمایش داده نمی‌شه** و فقط ترتیب رو
 *    تعیین می‌کنه: هر آیتم به یکی از تگ‌هاش «تعلق» می‌گیره — پرتکرارترینشون —
 *    و فقط همون یکی برای او امتیاز می‌گیره.
 *
 * چرا `mergeCount` لازم شد: تگ‌های ژانر (مایگریشن 0064) سلسله‌مراتب دارن ولی
 * دیتابیس این رو نمی‌دونه؛ «موسیقی راک» تقریباً همیشه کنارِ «موسیقی» می‌شینه.
 * با مرتب کردن روی `count` خام، هشت‌تای اولِ فهرست می‌شد «موسیقی، راک، پاپ،
 * جاز…» — یعنی هشت تا کاشی که همه‌شون یه چیزن. با این قاعده، رخدادِ
 * «موسیقی + راک» فقط به «موسیقی» (که پرتکرارتره) امتیاز می‌ده و «راک» تا
 * وقتی که خودش به‌تنهایی وزن نداشته باشه بالا نمی‌آد. نتیجه‌ش یه سلسله‌مراتبِ
 * خودکاره که از خودِ داده در می‌آد، نه از جدولی که کسی باید نگهش داره.
 */

// قاعده‌ی «categoryId هم خودش یه تگه» یه جا نوشته شده — همون‌جا صداش می‌زنیم.
import { tagIdsOf } from '../domain/types.ts';

/** هر چیزی که تگ دارد — رخداد یا مکان. */
export interface Taggable {
  tagIds?: number[] | null;
  categoryId?: number | null;
}

/**
 * رأیِ هر آیتم به کدام تگِ خودش برسد.
 *
 *  - `most` — به پرتکرارترین. سرشاخه‌ها بالا می‌آیند و زیرشاخه‌ای که همیشه
 *    کنارِ مادرش می‌آید هیچ‌وقت امتیاز نمی‌گیرد. روی دادهٔ اتاوا این یعنی
 *    ۱۵ تا از ۱۷ تگِ فرزند صفرند و «راک» با ۱۱۳ رخداد فقط ۱ امتیاز دارد.
 *  - `least` — به کم‌تکرارترین. دقیقاً برعکس: هر رخداد به **مشخص‌ترین**
 *    تگِ خودش تعلق می‌گیرد، پس ردیف پر می‌شود از ژانرها و «موسیقی» فقط
 *    وقتی امتیاز می‌گیرد که رخدادی جز آن چیزی نداشته باشد.
 *
 * هیچ‌کدام «درست» نیست؛ دو جورِ خواندنِ یک شهرند و انتخابش با ادمین است
 * (مایگریشن 0088). عددِ نمایشیِ کنارِ آیکون‌ها هیچ‌کدامشان را نمی‌بیند —
 * آن همیشه `count` خام است.
 */
export type MergePick = 'most' | 'least';

export interface TagTally {
  id: number;
  /** چند آیتم این تگ را دارند. همین عدد کنارِ آیکون نشان داده می‌شود. */
  count: number;
  /** فقط برای ترتیب — نگاه کن به توضیحِ بالای فایل. */
  mergeCount: number;
  /** چند تا از آن آیتم‌ها **هیچ تگِ دیگری** ندارند. */
  solo: number;
}

/** چند دسته روی صفحه‌ی اول می‌مانند؛ بقیه پشتِ دکمه‌ی «بیشتر». */
export const TOP_TAGS = 8;

/**
 * تگ‌هایی که هیچ‌وقت روی صفحه‌ی اول نمی‌آیند، هرچقدر هم پرتکرار باشند.
 *
 * «سایر» یعنی «نتوانستیم دسته‌بندی کنیم» — یک برچسبِ فنی، نه چیزی که کسی
 * دنبالش بگردد. با این حال بزرگ‌ترین تگِ فهرست است (هرچه کرالر نفهمد این‌جا
 * می‌افتد)، پس بدونِ این قاعده همیشه یکی از هشت کاشیِ اول را می‌گرفت.
 * حذف نمی‌شود — پشتِ «بیشتر» می‌ماند.
 */
export const ALWAYS_MORE_SLUGS = ['other'];

/**
 * تگ‌های ناشناخته را از هر آیتم بردار — و آیتمی که هیچ تگی برایش نمی‌ماند
 * زیرِ «سایر» بگذار.
 *
 * «ناشناخته» دو جور است و هر دو یک معنی دارند: تگی که ادمین کنارش گذاشته
 * (مایگریشن 0086)، و تگی که روی یه ردیفِ کش‌شده مونده ولی دیگه توی جدول
 * نیست. هیچ‌کدوم اسم و آیکون ندارن، پس نه چیپی می‌شن نه قابلِ زدن.
 *
 * چرا همین‌جا و نه بعدِ شمارش: رخدادی که **همه‌ی** تگ‌هاش کنار گذاشته شدن
 * وگرنه از ردیفِ دسته‌ها کاملاً بیرون می‌افتاد — توی فهرست دیده می‌شد ولی
 * از هیچ دسته‌ای پیدا نمی‌شد. «سایر» دقیقاً برای همینه: «نتونستیم
 * دسته‌بندی کنیم». و چون این یه قاعده‌ی **نمایش** است و نه یه ستون توی
 * دیتابیس، برگردوندنِ تگ همه‌چیز رو همون لحظه به حالتِ اول برمی‌گردونه.
 *
 * یه نکته‌ی ظریف: آیتمی که یه تگِ زنده و یه تگِ کنارگذاشته دارد به «سایر»
 * نمی‌رود — فقط تگِ کنارگذاشته‌اش می‌افتد. «سایر» مالِ آیتمی است که هیچ
 * راهِ دیگری برای پیدا شدن ندارد.
 *
 * `fallbackId` که ندهی یا خودش ناشناخته باشد (مثلاً ادمین «سایر» را هم
 * کنار گذاشته)، آیتم بی‌تگ می‌ماند — همان رفتارِ قبلی.
 *
 * و یک چیز که فرقی نمی‌کند: آیتم تگش را از دست داده باشد یا از اول هیچ
 * تگی نداشته، هر دو یک وضعیت‌اند و هر دو زیرِ «سایر» می‌روند. «سایر» یعنی
 * «تگ ندارد» — نه یک تگ که به رخداد بچسبد. برای همین است که این‌جا حساب
 * می‌شود و نه با نوشتن در دیتابیس.
 */
export function withKnownTags(
  pool: readonly Taggable[],
  known: ReadonlySet<number>,
  fallbackId?: number | null,
): Taggable[] {
  const fallback = fallbackId != null && known.has(fallbackId) ? [fallbackId] : [];
  return pool.map((item) => {
    const ids = tagIdsOf(item).filter((id) => known.has(id));
    // `categoryId: null` چون همه‌ی تگ‌ها — از جمله خودِ categoryId — همین‌جا
    // توی `tagIds` جمع شدن؛ گذاشتنش دوباره یعنی دوباره شمردنِ همون تگ.
    return { tagIds: ids.length ? ids : fallback, categoryId: null };
  });
}

/**
 * شمارشِ کاملِ یک فهرست.
 *
 * خروجی از قبل مرتب‌شده‌ست: اول `mergeCount`، بعد `count`، و در تساوی
 * شناسه‌ی کوچک‌تر. شناسه‌ی کوچک‌تر تصادفی نیست — تگ‌های پایه‌ی اپ
 * (طبیعت، غذا، ورزش…) از مایگریشنِ اول‌ان و ژانرها خیلی بعدتر اضافه شدن،
 * پس «کوچک‌تر» در عمل یعنی «عمومی‌تر»، و همون چیزیه که در تساوی می‌خوایم.
 *
 * تگ‌هایی که هیچ آیتمی ندارن اصلاً بیرون نمی‌آن: صفحه‌ی کشف فقط چیزی رو
 * نشون می‌ده که واقعاً داریم.
 *
 * و یه قاعده‌ی دوم برای همون هدف: **تگِ تک‌آیتمی هم بیرون می‌ره**، مگر
 * وقتی همون یه آیتم هیچ تگِ دیگه‌ای نداشته باشه. یه رخدادِ هشت‌تگی، هشت تا
 * تگِ «۱» می‌سازه که هیچ‌کدوم چیزی درباره‌ی شهر نمی‌گن و فقط ته فهرست رو پر
 * می‌کنن؛ ولی اگه اون رخداد راهِ دیگه‌ای برای پیدا شدن نداشته باشه، تنها
 * تگش باید بمونه وگرنه از فهرستِ دسته‌ها ناپدید می‌شه.
 */
export function tallyTags(
  pool: readonly Taggable[],
  opts: { pick?: MergePick } = {},
): TagTally[] {
  const { pick = 'most' } = opts;
  const count = new Map<number, number>();
  const solo = new Map<number, number>();
  for (const item of pool) {
    const ids = tagIdsOf(item);
    for (const id of ids) count.set(id, (count.get(id) ?? 0) + 1);
    if (ids.length === 1) solo.set(ids[0], (solo.get(ids[0]) ?? 0) + 1);
  }

  const mergeCount = new Map<number, number>();
  for (const item of pool) {
    const ids = tagIdsOf(item);
    if (ids.length === 0) continue;
    /*
     * برنده بینِ تگ‌های همین آیتم.
     *
     * تساوی هم قرینه‌ی خودِ قاعده شکسته می‌شود: در `most` شناسه‌ی کوچک‌تر
     * (تگ‌های پایه‌ی اپ از مایگریشنِ اول‌اند و ژانرها خیلی بعدتر، پس
     * «کوچک‌تر» در عمل یعنی «عمومی‌تر»)، و در `least` بزرگ‌تر — که به همان
     * دلیل یعنی «مشخص‌تر». وگرنه `least` در تساوی باز هم عمومی را انتخاب
     * می‌کرد و نصفِ کارش را خنثی می‌کرد.
     */
    let winner = ids[0];
    for (const id of ids) {
      const diff = (count.get(id) ?? 0) - (count.get(winner) ?? 0);
      const better = pick === 'most' ? diff > 0 : diff < 0;
      const tie = diff === 0 && (pick === 'most' ? id < winner : id > winner);
      if (better || tie) winner = id;
    }
    mergeCount.set(winner, (mergeCount.get(winner) ?? 0) + 1);
  }

  return [...count.entries()]
    .map(([id, c]) => ({
      id, count: c, mergeCount: mergeCount.get(id) ?? 0, solo: solo.get(id) ?? 0,
    }))
    // امتیازِ مژرِ تگِ حذف‌شده هم با خودش می‌ره و به کسی نمی‌رسه؛ حداکثر ۱ بوده
    // (تگِ تک‌آیتمی بیشتر از یه آیتم برای بردن نداره).
    .filter((x) => x.count > 1 || x.solo > 0)
    .sort((a, b) => (b.mergeCount - a.mergeCount) || (b.count - a.count) || (a.id - b.id));
}

/**
 * سلسله‌مراتبِ دستیِ ادمین (مایگریشن 0084): تا وقتی مادر روی صفحه‌ست،
 * فرزندهاش نمی‌آن.
 *
 * چرا لازم شد در حالی که مژر هم همین کار رو می‌کنه: مژر یه **حدسِ آماری**
 * است و فقط تا جایی درست می‌مونه که داده همراهی رو نشون بده. «راک» که ۹۳
 * رخداد داره و فقط نصفشون تگِ «موسیقی» هم دارن، از دستِ مژر در می‌ره و
 * کنارِ مادرش می‌شینه. این‌جا ادمین صریح می‌گه کی زیرِ کیه.
 *
 * استثنا — و همون چیزیه که این قاعده رو مفید می‌کنه: وقتی کاربر روی خودِ
 * مادر فیلتر کرده، فرزندها **باز می‌شن**. یعنی ردیف دو طبقه می‌شه: اول
 * سرشاخه‌ها، بعد از یه لمس، زیرشاخه‌های همون یکی.
 *
 * `parentOf` نگاشتِ «تگ → مادرش» است؛ تگی که توش نیست مادر ندارد.
 */
export function applyHierarchy<T extends { id: number }>(
  all: readonly T[],
  parentOf: ReadonlyMap<number, number | null | undefined>,
  selected: readonly number[] = [],
): T[] {
  const present = new Set(all.map((x) => x.id));
  return all.filter((x) => {
    // تگی که خودش انتخاب شده هیچ‌وقت پنهان نمی‌شه، وگرنه راهِ خاموش کردنش
    // هم با خودش می‌ره — همون قاعده‌ی `selected` در topTags.
    if (selected.includes(x.id)) return true;
    // از خودش به بالا برو: اولین سرشاخه‌ای که یا انتخاب شده یا روی صفحه‌ست،
    // تکلیف رو روشن می‌کنه.
    const seen = new Set<number>([x.id]);
    let p = parentOf.get(x.id);
    while (p != null && !seen.has(p)) {
      if (selected.includes(p)) return true;
      if (present.has(p)) return false;
      seen.add(p);
      p = parentOf.get(p);
    }
    return true;
  });
}

/**
 * تگ‌های پس‌زده را ببر ته فهرست، هرچقدر هم پرتکرار باشند.
 *
 * `topTags` آن‌ها را از صفحه‌ی اول **حذف** می‌کند، ولی فهرستِ کامل (پشتِ
 * «بیشتر») همچنان بر اساسِ امتیاز مرتب است — و چون «سایر» بزرگ‌ترین تگِ
 * فهرست است، همان‌جا می‌رفت اولِ صف. «سایر» یعنی «نتوانستیم دسته‌بندی
 * کنیم»؛ اولین چیزی که کاربر می‌بیند نباید همین باشد، هر عددی هم که کنارش
 * باشد.
 *
 * قبل از `topTags` صدا زده می‌شود، پس اگر خودِ «سایر» انتخاب شده باشد و به
 * همین دلیل روی صفحه‌ی اول بماند، باز هم آخرین چیپ است.
 *
 * ترتیبِ بقیه دست نمی‌خورد، و بینِ خودِ پس‌زده‌ها هم همان ترتیبِ ورودی
 * می‌ماند — این تابع فقط جابه‌جا می‌کند، مرتب نمی‌کند.
 */
export function demoteLast<T extends { id: number }>(
  all: readonly T[], demote: readonly number[] = [],
): T[] {
  if (demote.length === 0) return [...all];
  const tail = all.filter((x) => demote.includes(x.id));
  if (tail.length === 0) return [...all];
  return [...all.filter((x) => !demote.includes(x.id)), ...tail];
}

/**
 * هشت‌تای اول — با دو استثنا.
 *
 *  - `selected`: تگی که کاربر زده همیشه می‌مونه، حتی اگه از هشت‌تا بیرون
 *    باشه. اگه ناپدید شه، تنها راهِ خاموش کردنش هم ناپدید شده — و فهرستی
 *    که فیلترِ نامرئی داره یعنی «اپ خرابه».
 *  - `demote`: تگی که هیچ‌وقت نباید صفحه‌ی اول رو بگیره (نگاه کن به
 *    ALWAYS_MORE_SLUGS). جاش رو نفرِ بعدی می‌گیره، پس صفحه‌ی اول باز هم
 *    هشت‌تاست. مگر اینکه خودش انتخاب شده باشه، که اون‌وقت باید دیده بشه.
 */
export function topTags<T extends { id: number }>(all: readonly T[], opts: {
  selected?: readonly number[];
  demote?: readonly number[];
  limit?: number;
} = {}): T[] {
  const { selected = [], demote = [], limit = TOP_TAGS } = opts;
  const head = all
    .filter((x) => !demote.includes(x.id) || selected.includes(x.id))
    .slice(0, limit);
  const shown = new Set(head.map((x) => x.id));
  return [...head, ...all.filter((x) => selected.includes(x.id) && !shown.has(x.id))];
}
